import axios from 'axios';
import config from '@/config';
import { API_TIMEOUT } from '@/shared/constants/timing';
import type { SetUserOrganisationParams } from '../types';

const SCOPE = 'xelacore-auth xelacore-catalog xelacore-ingest xelacore-files xelacore-redemption xelacore-admin';

function serializeObject(obj: Record<string, string>): string {
  const str: string[] = [];
  for (const p in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
    }
  }
  return str.join('&');
}

export interface UserOrganisation {
  organisation_id: string;
  DepartmentName: string;
  modules?: string[];
}

export interface FetchUserOrganisationsResponse {
  _metadata?: Record<string, unknown>;
  status?: string;
  data?: {
    organisations: UserOrganisation[];
  };
  organisations?: UserOrganisation[]; // fallback for direct response
}

export interface SetUserOrganisationResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

/**
 * Fetches the list of organisations a user belongs to
 */
export async function fetchUserOrganisations(
  userToken: string
): Promise<FetchUserOrganisationsResponse> {
  const response = await axios.request<FetchUserOrganisationsResponse>({
    url: `${config.get('AUTH_URL')}organisations/`,
    method: 'get',
    baseURL: config.get('AUTH_BASE_URL'),
    headers: { authorization: `Bearer ${userToken}` },
    params: {
      page: 1,
      page_size: 100000,
    },
    timeout: API_TIMEOUT,
  });

  return response.data;
}

/**
 * Sets the bearer token based on a user's organisation
 * This calls /oauth/token with the refresh-organisation grant type
 */
export async function setUserOrganisation({
  orgId,
  refreshToken,
}: SetUserOrganisationParams): Promise<SetUserOrganisationResponse> {
  const postBody: Record<string, string> = {
    grant_type: orgId
      ? 'urn:xelacore.io:oauth2:refresh-organisation'
      : 'urn:xelacore.io:oauth2:refresh',
    refresh_token: refreshToken,
    client_id: config.get('CLIENT_ID'),
    redirect_uri: config.get('REDIRECT_URL'),
    scope: SCOPE,
  };

  if (orgId) {
    postBody.organisation_id = orgId;
  }

  const response = await axios.request<SetUserOrganisationResponse>({
    url: `${config.get('OAUTH_URL')}token`,
    method: 'post',
    baseURL: config.get('AUTH_BASE_URL'),
    data: serializeObject(postBody),
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    timeout: API_TIMEOUT,
  });

  return response.data;
}

export async function logout(): Promise<void> {
  try {
    await axios.get(`${config.get('API_BASE_URL')}${config.get('OAUTH_URL')}logout`, { timeout: API_TIMEOUT });
  } catch {
    // Ignore logout errors
  }
}
